import { Injectable } from '@angular/core';
import { BackendService } from './general/backend.service';
import { TranslateService } from '@ngx-translate/core';
import { HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class SurveyService {
  constructor(private backend: BackendService, private translateService: TranslateService) {
  }

  private OPENED_QUESTION = 'OPENED';
  private QUANTITATIVE_QUESTION = 'QUANTITATIVE';
  private CHECKBOX_QUESTION = 'CHECKBOX';
  private MULTIPLE_CHOICE_QUESTION = 'MULTIPLE_CHOICE';
  private CONSENSUS_QUESTION = 'CONSENSUS';
  private GRID_QUESTION = 'GRID';

  public surveySubmittedTypologies = {
    IS_NEW: 'IS_NEW',
    IS_NEW_DRAFT: 'IS_NEW_DRAFT',
    IS_NEW_REVISION: 'IS_NEW_REVISION',
    IS_NEW_SESSION: 'IS_NEW_SESSION',
  };

  public questionFinalDecisionStatuses = [
    {
      id: 'accepted',
      title: this.translateService.instant('service.survey-service.question-final-decision-statuses.accepted.title'),
      titleNoRev: this.translateService.instant('service.survey-service.question-final-decision-statuses.accepted.title-no-rev'),
      icon: 'check_circle',
      bgcolor: '#00bc1d',
    }

    ,
    {
      id: 'reproposed',
      title: this.translateService.instant('service.survey-service.question-final-decision-statuses.reproposed.title'),
      titleNoRev: this.translateService.instant('service.survey-service.question-final-decision-statuses.reproposed.title-no-rev'),
      icon: 'autorenew',
      bgcolor: '#FFAD4A'
    }
    ,
    {
      id: 'revised',
      title: this.translateService.instant('service.survey-service.question-final-decision-statuses.revised.title'),
      titleNoRev: this.translateService.instant('service.survey-service.question-final-decision-statuses.revised.title-no-rev'),
      icon: 'edit',
      bgcolor: '#1e88e5'
    }
    ,
    {
      id: 'revised_split',
      title: this.translateService.instant('service.survey-service.question-final-decision-statuses.revised_split.title'),
      titleNoRev: this.translateService.instant('service.survey-service.question-final-decision-statuses.revised_split.title-no-rev'),
      icon: 'call_split',
      bgcolor: '#8e24aa'
    }
    ,
    {
      id: 'rejected',
      title: this.translateService.instant('service.survey-service.question-final-decision-statuses.rejected.title'),
      titleNoRev: this.translateService.instant('service.survey-service.question-final-decision-statuses.rejected.title-no-rev'),
      icon: 'cancel',
      bgcolor: '#ff0000'
    }
  ];

  /**
   * Recupera l'icona associata allo stato della decisione.
   * @param decisionId L'ID della decisione (es. 'accepted', 'rejected')
   * @returns La stringa dell'icona o un valore di default.
   */
  getDecisionIcon(decisionId: string): string {
    const status = this.questionFinalDecisionStatuses.find(item => item.id === decisionId);
    return status ? status.icon : 'help_outline';
  }

  /**
   * Recupera il colore di sfondo associato allo stato della decisione.
   * @param decisionId L'ID della decisione (es. 'accepted', 'rejected')
   * @returns Il codice colore HEX o un valore di default.
   */
  getDecisionColor(decisionId: string): string {
    const status = this.questionFinalDecisionStatuses.find(item => item.id === decisionId);
    return status ? status.bgcolor : 'transparent';
  }


  public questionTypologies = [
    {
      id: this.OPENED_QUESTION,
      title: this.translateService.instant('service.survey-service.question-typologies.opened.title'),
      description: this.translateService.instant('service.survey-service.question-typologies.opened.description'),
    },
    {
      id: this.QUANTITATIVE_QUESTION,
      title: this.translateService.instant('service.survey-service.question-typologies.quantitative.title'),
      description: this.translateService.instant('service.survey-service.question-typologies.quantitative.description'),
    },
    {
      id: this.CHECKBOX_QUESTION,
      title: this.translateService.instant('service.survey-service.question-typologies.checkbox.title'),
      description: this.translateService.instant('service.survey-service.question-typologies.checkbox.description'),
    },
    {
      id: this.MULTIPLE_CHOICE_QUESTION,
      title: this.translateService.instant('service.survey-service.question-typologies.multiple-choice.title'),
      description: this.translateService.instant('service.survey-service.question-typologies.multiple-choice.description'),
    },
    {
      id: this.CONSENSUS_QUESTION,
      title: this.translateService.instant('service.survey-service.question-typologies.consensus.title'),
      description: this.translateService.instant('service.survey-service.question-typologies.consensus.description'),
    },
    {
      id: this.GRID_QUESTION,
      title: this.translateService.instant('service.survey-service.question-typologies.grid.title'),
      description: this.translateService.instant('service.survey-service.question-typologies.grid.description'),
    },
  ];

  public selectOptions = [{
    key: 'use_description',
    label: 'modals.modal-survey-details.add-description',
    enabledTypologies: [
      this.CONSENSUS_QUESTION,
      this.MULTIPLE_CHOICE_QUESTION,
      this.CHECKBOX_QUESTION,
      this.OPENED_QUESTION,
      this.QUANTITATIVE_QUESTION,
      this.GRID_QUESTION
    ]
  },
  {
    key: 'feedback_is_mandatory',
    label: 'modals.modal-survey-details.mandatory-feedback',
    enabledTypologies: [
      this.MULTIPLE_CHOICE_QUESTION,
      this.CHECKBOX_QUESTION,
      this.QUANTITATIVE_QUESTION,
      this.OPENED_QUESTION,
      this.GRID_QUESTION,
      this.CONSENSUS_QUESTION
    ]
  },
    {
      organizationFeature: 'google_maps_integration',
      key: 'use_google_maps',
      label: 'modals.modal-survey-details.use_google_maps',
      enabledTypologies: [
        this.OPENED_QUESTION,
      ]
    },
    {
      key: 'use_cascade_select',
      label: 'modals.modal-survey-details.use-cascade-select',
      enabledTypologies: [
        this.MULTIPLE_CHOICE_QUESTION,
        this.CHECKBOX_QUESTION
      ]
    },
  ];

  public getQuestionFinalDecisionStatuses(): object {
    return this.questionFinalDecisionStatuses;
  }

  public isQuestionType(type: string, questionType: string): boolean {
    if (type == null || typeof type === 'undefined') {
      return false;
    }
    return type.toUpperCase() === questionType.toUpperCase();
  }

  public isCheckboxQuestion(type: string): boolean {
    return this.isQuestionType(type, this.CHECKBOX_QUESTION);
  }

  public isConsensusQuestion(type: string): boolean {
    return this.isQuestionType(type, this.CONSENSUS_QUESTION);
  }

  public isQuantitativeQuestion(type: string): boolean {
    return this.isQuestionType(type, this.QUANTITATIVE_QUESTION);
  }

  public isOpenedQuestion(type: string): boolean {
    return this.isQuestionType(type, this.OPENED_QUESTION);
  }

  public isMultipleChoiceQuestion(type: string): boolean {
    return this.isQuestionType(type, this.MULTIPLE_CHOICE_QUESTION);
  }

  public isGridQuestion(type: string): boolean {
    return this.isQuestionType(type, this.GRID_QUESTION);
  }

  public surveyQuestionAnswerIsValid(question, highlightError: boolean = true) {
    let validation;

    if (this.isOpenedQuestion(question.typology)) {
      if (!question?.response?.answer) {
        if (question.required) {
          validation = { valid: false, message: 'pages.surveys.response.opened.not-answer' };
        } else {
          question.response.status = 'ABSTAINED';
        }
      }
    }

    if (question?.customs?.feedback_is_mandatory) {
      if (!question?.response?.feedback) {
        validation = { valid: false, message: 'modals.modal-survey-details.errors.feedback-is-mandatory' };
      }
    }

    if (this.isQuantitativeQuestion(question.typology)) {
      if (!question?.response?.answer) {
        if (question.required) {
          validation = { valid: false, message: 'pages.surveys.response.quantitative.not-answer' };
        } else {
          question.response.status = 'ABSTAINED';
        }
      } else {
        const answerAsNumber = parseFloat(question.response.answer);

        if (
          isNaN(answerAsNumber) ||
          answerAsNumber < parseFloat(question.customs.min) ||
          answerAsNumber > parseFloat(question.customs.max)
        ) {
          validation = { valid: false, message: 'pages.surveys.response.no-quantitative-answer-in-range' };
        }
      }
    }

    if (this.isMultipleChoiceQuestion(question.typology)) {
      if (!question?.response?.choice) {
        if (question.required) {
          validation = { valid: false, message: 'generic.proposal-option-valid' };
        } else {
          question.response.status = 'ABSTAINED';
        }
      } else {
        // Check if the selected choice has a mandatory open response
        const selectedChoice = question.choices.find(c => c.id === question.response.choice);
        if (selectedChoice && selectedChoice.customs && selectedChoice.customs.has_open_response && selectedChoice.customs.open_response_mandatory) {
          if (!question.response.customs || !question.response.customs.open_response_value) {
            validation = { valid: false, message: 'pages.surveys.response.opened.not-answer' }; // Reusing existing message or create a specific one
          }
        }
      }
    }

    if (this.isCheckboxQuestion(question.typology)) {
      if (!question?.choices.some((choice) => choice.checked)) {
        if (question.required) {
          validation = { valid: false, message: 'pages.surveys.response.no-checkbox-setted' };
        } else {
          question.response.status = 'ABSTAINED';
        }
      }
    }

    if (this.isConsensusQuestion(question.typology)) {
      if (question.required) {
        if (question.response.answer !== 'AGREE' && question.response.answer !== 'DISAGREE') {
          validation = { valid: false, message: 'pages.surveys.response.consensus.not-answer' };
        }
        if (!question.response.customs.confidence) {
          validation = { valid: false, message: 'pages.surveys.response.consensus.not-confidence' };
        }
      } else {
        if (question.response.answer !== 'AGREE' && question.response.answer !== 'DISAGREE' && question.response.answer !== 'ABSTAINED') {
          validation = { valid: false, message: 'pages.surveys.response.consensus.not-answer' };
        }
      }
    }

    if (this.isGridQuestion(question.typology)) {
      const allRowsAnswered = question.grid_rows.every(row =>
        question.grid_columns.some(col => col.id?.toString() === row.answer_id?.toString())
      );

      if (question.required) {
        if (!allRowsAnswered) {
          validation = {
            valid: false,
            message: 'pages.surveys.response.grid.not-answer'
          };
        }
      } else {
        if (!allRowsAnswered) {
          question.response.status = 'ABSTAINED';
        }
      }
    }

    if (validation && !validation.valid) {
      if (highlightError) {
        const el = document.getElementById(`accordion_question_${question.id}`);
        if (el) {
          question.validationError = true;
          el.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }
      return validation;
    }

    question.validationError = false;
    return { valid: true };
  }

  public surveyAnswerIsValid(survey, highlightError: boolean = true): object {
    for (const question of survey.questions) {
      const is_valid_response = this.surveyQuestionAnswerIsValid(question, highlightError);
      if (!is_valid_response?.valid) {
        return is_valid_response;
      }
    }
    return { valid: true };
  }

  public surveyIsValid(survey): object {
    for (const question of survey.questions) {
      if (!question?.question) {
        return { valid: false, message: 'modals.modal-survey-details.errors.title-is-required' };
      }

      if (this.isMultipleChoiceQuestion(question.typology) || this.isCheckboxQuestion(question.typology)) {
        if (question?.choices?.length > 1) {
          for (const choice of question.choices) {
            if (!choice?.title || choice?.title === '') {
              return { valid: false, message: 'modals.modal-survey-details.errors.choice-not-valid' };
            }
          }
        } else {
          return { valid: false, message: 'modals.modal-survey-details.errors.multiple-choices-length-errors' };
        }
      }

      if (this.isConsensusQuestion(question.typology) || this.isQuantitativeQuestion(question.typology)) {
        if (question?.customs?.use_custom_min_max_labels) {
          if (!question?.customs?.min_label || !question?.customs?.max_label) {
            return { valid: false, message: 'modals.modal-survey-details.errors.no-max-min-labels-error' };
          }
        }
      }

      if (this.isQuantitativeQuestion(question.typology)) {
        if (
          question?.customs?.max === null ||
          question.customs.min === null ||
          question?.customs?.max === undefined ||
          question.customs.min === undefined
        ) {
          return { valid: false, message: 'modals.modal-survey-details.errors.max-min-error' };
        }
        if (question?.customs?.min >= question?.customs?.max) {
          return { valid: false, message: 'modals.modal-survey-details.errors.min-error' };
        }
        if (!question?.customs?.measure_unit) {
          return { valid: false, message: 'modals.modal-survey-details.errors.not-measure-unit' };
        }
      }
    }
    return { valid: true };
  }

  public surveyDetails(survey_id: string, success_callback = (data: any) => {
  }, error_callback = (error: any) => {
  }) {

    this.backend.get(`surveys/${survey_id}/`).subscribe(result => {
      success_callback(result);
    }, (error) => {
      console.log(error);
      this.backend.showErrors(error);
      error_callback(error);
    });
  }

  public getSurveyRevisionPreview(surveyId: string): Observable<any> {
    return this.backend.get(`surveys/${surveyId}/revision-preview`);
  }

  public surveyResponses(
    survey_id: string,
    page: number = 1,
    per_page: number = 10,
    success_callback: (data: any) => void = (data) => {
    },
    error_callback: (error: any) => void = (error) => {
    }
  ) {
    const params = new HttpParams()
      .set('page', page.toString())
      .set('per_page', per_page.toString());

    this.backend.get(`surveys/${survey_id}/responses`, params)
      .subscribe(
        result => {
          success_callback(result);
        },
        error => {
          console.log(error);
          this.backend.showErrors(error);
          error_callback(error);
        }
      );
  }


  public getQuestionWithResponses(question_id: string, success_callback = (data: any) => {
  }, error_callback = (error: any) => {
  }) {

    this.backend.get(`surveys/question/${question_id}/responses`).subscribe(result => {
      success_callback(result);
    }, (error) => {
      console.log(error);
      this.backend.showErrors(error);
      error_callback(error);
    });
  }

  public surveyDetailsResponsePage(survey_id: string, params: object, success_callback = (data: any) => {
  }, error_callback = (error: any) => {
  }) {
    let url = `surveys/${survey_id}/?is_response_page=true`;

    for (const key in params) {
      if (params.hasOwnProperty(key)) {
        url += `&${encodeURIComponent(key)}=${encodeURIComponent(params[key])}`;
      }
    }

    this.backend.get(url).subscribe(result => {
      success_callback(result);
    }, (error) => {
      console.log(error);
      this.backend.showErrors(error);
      error_callback(error);
    });
  }

  public getSurveysByProjectId(project_id: string, success_callback = (data: any) => {
  }, error_callback = (error: any) => {
  }) {

    this.backend.get(`project/${project_id}/surveys/`).subscribe(result => {
      success_callback(result);
    }, (error) => {
      console.log(error);
      this.backend.showErrors(error);
      error_callback(error);
    });
  }

  public getSurveyHistoryByID(survey_history_id: string, success_callback = (data: any) => {
  }, error_callback = (error: any) => {
  }) {
    this.backend.get(`survey-histories/${survey_history_id}/`).subscribe(result => {
      success_callback(result);
    }, (error) => {
      console.log(error);
      this.backend.showErrors(error);
      error_callback(error);
    });
  }

  public getSurveyDashboard(survey_id: string, success_callback = (data: any) => {
  }, error_callback = (error: any) => {
  }) {

    this.backend.get(`surveys/${survey_id}/dashboard`).subscribe(result => {
      success_callback(result);
    }, (error) => {
      console.log(error);
      this.backend.showErrors(error);
      error_callback(error);
    });
  }

  public getSurveyQuestionsHistoriesBySurveyID(
    survey_history_id: string,
    page: number = 1,
    per_page: number = 10,
    success_callback: (data: any) => void = (data) => {
    },
    error_callback: (error: any) => void = (error) => {
    }
  ) {
    const params = new HttpParams()
      .set('page', page.toString())
      .set('per_page', per_page.toString());

    this.backend.get(`survey-histories/${survey_history_id}/survey-questions-histories`, params).subscribe(result => {
      success_callback(result);
    }, (error) => {
      console.log(error);
      this.backend.showErrors(error);
      error_callback(error);
    });
  }

  public changeSurveyStatus(survey_id: string, model: any, success_callback = (data: any) => {
  }, error_callback = (error: any) => {
  }) {

    this.backend.post(`surveys/${survey_id}/change-status/`, model).subscribe(result => {
      success_callback(result);
    }, (error) => {
      console.log(error);
      this.backend.showErrors(error);
      error_callback(error);
    });
  }

  public updateQuestionFinalDecision(survey_id: string, question_id: string, model: any, success_callback = (data: any) => {
  }, error_callback = (error: any) => {
  }) {

    this.backend.post(`surveys/${survey_id}/questions/${question_id}/update-final-decision/`, model).subscribe(result => {
      success_callback(result);
    }, (error) => {
      console.log(error);
      this.backend.showErrors(error);
      error_callback(error);
    });
  }


  public createSurvey(project_id: string, model: any, success_callback = (data: any) => {
  }, error_callback = (error: any) => {
  }) {

    this.backend.post(`project/${project_id}/surveys/`, model).subscribe(result => {
      success_callback(result);
    }, (error) => {
      console.log(error);
      this.backend.showErrors(error);
      error_callback(error);
    });
  }

  public createSurveyRevision(survey_id: any) {
    return this.backend.post(`surveys/${survey_id}/revise/`, null);
  }

  public deleteSurvey(survey_id: string, success_callback = (data: any) => {
  }, error_callback = (error: any) => {
  }) {

    this.backend.post(`surveys/${survey_id}/delete`, survey_id).subscribe(result => {
      success_callback(result);
    }, (error) => {
      console.log(error);
      this.backend.showErrors(error);
      error_callback(error);
    });
  }

  public createSurveyAnswer(survey_id: string, model: any, success_callback = (data: any) => {
  }, error_callback = (error: any) => {
  }) {

    this.backend.post(`surveys/${survey_id}/responses/`, model).subscribe(result => {
      success_callback(result);
    }, (error) => {
      console.log(error);
      this.backend.showErrors(error);
      error_callback(error);
    });
  }

  public downloadResponsesCSV(survey_id: string, success_callback, error_callback) {
    this.backend.download(
      `surveys/${survey_id}/export/responses`,
      'surveys_responses',
      success_callback,
      error_callback
    );
  }

  public downloadSurveyTeamData(survey_id, payload, success_callback, error_callback) {
    this.backend.payloadDownload(
      `surveys/${survey_id}/export/team-data`,
      `Survey ${survey_id} Team Data`,
      payload,
      success_callback,
      error_callback
    );
  }

  /**
   * Avvia una nuova sessione di decisione
   * @param organization_project_id
   * @param survey_id
   * @param model
   * @param success_callback
   * @param error_callback
   */
  public addNewSurveyTeamMember(
    survey_id: string,
    model: any,
    success_callback = (data: any) => {
    },
    error_callback = (error: any) => {
    }
  ) {
    this.backend
      .post(`surveys/${survey_id}/add-new-survey-team-member`, model)
      .subscribe(
        (result) => {
          success_callback(result);
        },
        (error) => {
          console.log(error);
          this.backend.showErrors(error) ;
          error_callback(error);
        }
      );
  }

  public updateSurveyTeamMember(
    survey_id: string,
    member_id: string,
    model: any,
    success_callback = (data: any) => {
    },
    error_callback = (error: any) => {
    }
  ) {
    this.backend
      .post(`surveys/${survey_id}/survey-team-members/${member_id}/update`, model)
      .subscribe(
        (result) => {
          success_callback(result);
        },
        (error) => {
          console.log(error);
          this.backend.showErrors(error);
          error_callback(error);
        }
      );
  }

  public bulkAddSurveyTeamMembers(
    survey_id: string,
    members: any[],
    success_callback = (data: any) => {
    },
    error_callback = (error: any) => {
    }
  ) {
    this.backend
      .post(`surveys/${survey_id}/survey-team-members/bulk-add`, { members })
      .subscribe(
        (result) => {
          success_callback(result);
        },
        (error) => {
          console.log(error);
          this.backend.showErrors(error);
          error_callback(error);
        }
      );
  }

  public importSurveyTeamPreview(
    survey_id: string,
    file: File,
    success_callback = (data: any) => {
    },
    error_callback = (error: any) => {
    }
  ) {
    const formData = new FormData();
    formData.append('file', file);

    this.backend
      .postFormData(`surveys/${survey_id}/survey-team-members/import-preview`, formData)
      .subscribe(
        (result) => {
          success_callback(result);
        },
        (error) => {
          console.log(error);
          this.backend.showErrors(error);
          error_callback(error);
        }
      );
  }

  public bulkAddSurveyTeamMembersImport(
    survey_id: string,
    file: File,
    mapping: any,
    startRow: number,
    endRow: number | null,
    defaultRole: string,
    success_callback = (data: any) => {
    },
    error_callback = (error: any) => {
    }
  ) {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('mapping', JSON.stringify(mapping));
    formData.append('start_row', startRow.toString());
    if (endRow) formData.append('end_row', endRow.toString());
    if (defaultRole) formData.append('default_role', defaultRole);

    this.backend
      .postFormData(`surveys/${survey_id}/survey-team-members/import`, formData)
      .subscribe(
        (result) => {
          success_callback(result);
        },
        (error) => {
          console.log(error);
          this.backend.showErrors(error);
          error_callback(error);
        }
      );
  }

  public getEmailResponsesForSurveyTeamMember(

    survey_id,
    idtm: string,
    success_callback = (data: any) => {
    },
    error_callback = (error: any) => {
    }
  ) {
    this.backend
      .get(`surveys/${survey_id}/survey-team-member/${idtm}/email-responses/`)
      .subscribe(
        (result) => {
          success_callback(result);
        },
        (error) => {
          console.log(error);
          this.backend.showErrors(error);
          error_callback(error);
        }
      );
  }

  public resendInvitations(id_survey, list_team_members, success_callback = (data: any) => {
  }, error_callback = (error: any) => {
  }) {

    this.backend.post(`surveys/${id_survey}/survey-team-members/resend-invitations/`, list_team_members).subscribe(result => {
      success_callback(result);
    }, (error) => {
      console.log(error);
      this.backend.showErrors(error);
      error_callback(error);
    }
    );
  }

  public deleteTeamMembers(id_survey, list_team_members, success_callback = (data: any) => {
  }, error_callback = (error: any) => {
  }) {

    this.backend.post(`surveys/${id_survey}/survey-team-members/delete/`, list_team_members).subscribe(result => {
      success_callback(result);
    }, (error) => {
      console.log(error);
      this.backend.showErrors(error);
      error_callback(error);
    }
    );
  }

  /**
   * Check if there are attachments being processed for a survey
   * @param survey_id The ID of the survey
   * @param success_callback Callback with the processing status
   * @param error_callback Error callback
   */
  public checkProcessingAttachments(
    survey_id: string,
    success_callback = (data: any) => {},
    error_callback = (error: any) => {}
  ) {
    this.backend.get(`surveys/${survey_id}/check-processing-attachments`).subscribe(
      (result) => {
        success_callback(result);
      },
      (error) => {
        console.log(error);
        this.backend.showErrors(error);
        error_callback(error);
      }
    );
  }

  /**
   * Get all processed attachments LLM analysis for a question
   *
   * Returns all processed attachments at three levels:
   * 1. Survey-level attachments (associated with survey_id)
   * 2. Question-level attachments (associated with survey_question_id)
   * 3. Answer-level attachments (associated with answers to the question)
   *
   * @param survey_id The ID of the survey
   * @param survey_question_id The ID of the survey question
   * @param survey_question_answer_id The ID of a specific answer (optional - if not provided, returns all answer attachments)
   * @param success_callback Callback with the processed attachments array
   * @param error_callback Error callback
   *
   * @example
   * // Get all attachments for survey 123, question 456 (including all answers)
   * getAttachmentsLLMAnalysis('123', '456', undefined, (data) => {...})
   *
   * // Get attachments for survey 123, question 456, specific answer 789
   * getAttachmentsLLMAnalysis('123', '456', '789', (data) => {...})
   */
  public getAttachmentsLLMAnalysis(
    survey_id: string,
    survey_question_id: string,
    survey_question_answer_id?: string,
    success_callback = (data: any) => {},
    error_callback = (error: any) => {}
  ) {
    let url = `surveys/${survey_id}/attachments-llm-analysis?survey_question_id=${survey_question_id}`;

    if (survey_question_answer_id) {
      url += `&survey_question_answer_id=${survey_question_answer_id}`;
    }

    this.backend.get(url).subscribe(
      (result) => {
        success_callback(result);
      },
      (error) => {
        console.log(error);
        this.backend.showErrors(error);
        error_callback(error);
      }
    );
  }

  public addTeamMemberCustomField(
    survey_id: string | number,
    payload: { name: string; type: string; visible?: boolean; required?: boolean },
    success_callback = (data: any) => {},
    error_callback = (error: any) => {}
  ) {
    this.backend.post(`surveys/${survey_id}/team-members-custom-field`, payload).subscribe(
      (result) => { success_callback(result); },
      (error) => {
        console.log(error);
        this.backend.showErrors(error);
        error_callback(error);
      }
    );
  }

  public deleteTeamMemberCustomField(
    survey_id: string | number,
    custom_field_id: string | number,
    success_callback = (data: any) => {},
    error_callback = (error: any) => {}
  ) {
    this.backend.destroy(`surveys/${survey_id}/team-members-custom-field/${custom_field_id}`).subscribe(
      (result) => { success_callback(result); },
      (error) => {
        console.log(error);
        this.backend.showErrors(error);
        error_callback(error);
      }
    );
  }

  public updateTeamMemberCustomField(
    survey_id: string | number,
    custom_field_id: string | number,
    payload: { name: string; type: string; visible?: boolean; required?: boolean },
    success_callback = (data: any) => {},
    error_callback = (error: any) => {}
  ) {
    this.backend.put(`surveys/${survey_id}/team-members-custom-field/${custom_field_id}`, payload).subscribe(
      (result) => { success_callback(result); },
      (error) => {
        console.log(error);
        this.backend.showErrors(error);
        error_callback(error);
      }
    );
  }
}
